import { useEffect, useState } from 'react';
import { KeyRound, LockKeyhole } from 'lucide-react';
import { PIN_LENGTH, isValidPin } from '../../core/pin';
import { AppSignature, AuthShell } from './AuthShell';
import { CodeInput } from './CodeInput';

interface PinSetupStepProps {
  /** `setup` asks for a PIN twice, `unlock` verifies an existing one. */
  mode: 'setup' | 'unlock';
  fullName?: string;
  onSubmit: (pin: string) => void;
  /** Escape hatch: "not you?" / "sign in with another number". */
  onCancel: () => void;
  busy?: boolean;
  error?: string | null;
}

/** Step 3 — 4-digit PIN for fast offline unlocks (setup and unlock). */
export function PinSetupStep({
  mode,
  fullName,
  onSubmit,
  onCancel,
  busy = false,
  error = null,
}: PinSetupStepProps) {
  const [pin, setPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [stage, setStage] = useState<'enter' | 'confirm'>('enter');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!error) return;
    setPin('');
    setConfirmation('');
    setStage('enter');
  }, [error]);

  const isSetup = mode === 'setup';
  const confirming = isSetup && stage === 'confirm';
  const value = confirming ? confirmation : pin;

  const handleComplete = (code: string) => {
    setLocalError(null);
    if (!isSetup) {
      onSubmit(code);
      return;
    }
    if (stage === 'enter') {
      if (!isValidPin(code)) return;
      setStage('confirm');
      setConfirmation('');
      return;
    }
    if (code !== pin) {
      setLocalError('הקודים אינם תואמים — נסו שוב');
      setPin('');
      setConfirmation('');
      setStage('enter');
      return;
    }
    onSubmit(code);
  };

  const shown = localError ?? error;

  return (
    <AuthShell
      icon={isSetup ? <KeyRound size={22} aria-hidden /> : <LockKeyhole size={22} aria-hidden />}
      title={isSetup ? 'הגדרת קוד אישי' : `שלום${fullName ? ` ${fullName}` : ''}`}
      subtitle={
        isSetup
          ? confirming
            ? 'הזינו שוב את הקוד לאישור'
            : `בחרו קוד בן ${PIN_LENGTH} ספרות לכניסה מהירה גם ללא רשת`
          : 'הזינו את הקוד האישי לכניסה'
      }
      footer={<AppSignature />}
    >
      <CodeInput
        length={PIN_LENGTH}
        value={value}
        onChange={confirming ? setConfirmation : setPin}
        onComplete={handleComplete}
        label={confirming ? 'אימות קוד אישי' : 'קוד אישי'}
        disabled={busy}
        invalid={Boolean(shown)}
        secret
      />

      {shown ? <p className="mt-3 text-sm text-red-600 text-center">{shown}</p> : null}

      <button
        type="button"
        onClick={() => handleComplete(value)}
        disabled={busy || value.length !== PIN_LENGTH}
        className="mt-5 w-full h-12 rounded-xl bg-brand-500 text-white font-semibold transition-colors hover:bg-brand-700 disabled:opacity-40 disabled:hover:bg-brand-500"
      >
        {busy ? 'רגע…' : isSetup ? (confirming ? 'שמירה' : 'המשך') : 'כניסה'}
      </button>

      <button
        type="button"
        onClick={onCancel}
        className="mt-4 w-full text-sm text-ink/60 hover:text-ink"
      >
        {isSetup ? 'ביטול' : 'כניסה עם מספר אחר'}
      </button>
    </AuthShell>
  );
}
